import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ISvzStatusBadge } from './interfaces/svz-status-badge.interface';

export type SvzStatusBadgeVariant = 'default' | 'design-system' | 'pill' | 'card';

const DESIGN_SYSTEM_BADGE_CLASSES: Record<string, string> = {
  active: 'bg-[#DCF5E6] text-[#14AE5C] border-[#14AE5C]',
  inactive: 'bg-[#EEF0F4] text-[#5A5F6B] border-[#5A5F6B]',
};

/**
 * Record statuses of the design system (`--svc-st-*` groups of lib-shared-core `_design-system-tokens.scss`; the
 * prototypes' `PST` map, Alerts/v1.1 5155): tint fill, solid text and border.
 */
const RECORD_STATUS_BADGE_CLASSES: Record<string, string> = {
  late: 'bg-[#FDE7E7] text-[#EC221F] border-[#EC221F]',
  delayed: 'bg-[#FDE7E7] text-[#EC221F] border-[#EC221F]',
  ontime: 'bg-[#FDF3D6] text-[#A07D12] border-[#A07D12]',
  approval: 'bg-[#F5F5FF] text-[#001391] border-[#001391]',
  execution: 'bg-[#EBDAC3] text-[#653A01] border-[#653A01]',
  completed: 'bg-[#DCF5E6] text-[#14AE5C] border-[#14AE5C]',
  done: 'bg-[#DCF5E6] text-[#14AE5C] border-[#14AE5C]',
  closed: 'bg-[#EEF0F2] text-[#71767C] border-[#71767C]',
  canceled: 'bg-[#EEF0F2] text-[#71767C] border-[#71767C]',
};

const CARD_BADGE_CLASSES: Record<string, string> = { ...DESIGN_SYSTEM_BADGE_CLASSES, ...RECORD_STATUS_BADGE_CLASSES };

@Component({
  selector: 'svz-status-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-status-badge.component.html',
  host: {
    class: 'grid w-fit self-center',
    '[class.min-w-18]': 'hasHostMinWidth()',
    '[class.w-full]': 'fullWidth()',
  },
  standalone: true,
  imports: [NgClass],
})
export class SvzStatusBadgeComponent {
  readonly textClass = input<string | null>(null);

  readonly badgeData = input<ISvzStatusBadge>({ description: '-', type: 'none' });

  readonly colorClass = input<string>(undefined);

  readonly truncate = input<boolean>(true);

  readonly fullWidth = input<boolean>(false);

  /** Additive, defaults to current behavior (rounded-lg, text-sm/font-normal, px-2 py-1). Set true
   *  to match prototypes whose status pill is compact — rounded-full, 12px/semibold, 4px/15px
   *  padding, 74px min-width — see RCA Type Registration. */
  readonly pill = input(false);

  /** Additive, defaults to current behavior. `'design-system'` renders the prototype `.status` pill — auto width from
   *  a 74px minimum, `padding:4px 15px`, 12px semibold on a 20px line-height (prototype `.status{line-height:20px}`
   *  since 01/10/2026; AB#73558 had measured the earlier normal line-height) — with the
   *  design-system colours per type — `active` `#DCF5E6`/`#14AE5C`, `inactive` `#EEF0F4`/`#5A5F6B` — so consumers stop
   *  passing `colorClass` for the register screens (AB#72295). Other types keep the legacy map; an explicit
   *  `colorClass` still wins. `'card'` renders the record card's status tag (`.kb-card .kc-st .pill`, Alerts/v1.1
   *  1362: 11px semibold on a 14px line, `padding:2px 9px`, 20px tall, no minimum width) with the design-system
   *  record statuses (`late`, `ontime`, `approval`, `execution`, `completed`, `closed`…) on top of `active` /
   *  `inactive` (AB#75402). */
  readonly variant = input<SvzStatusBadgeVariant>('default');

  /**
   * Solid colour of the status (text and border), as the prototype `SDS.pill(label, { solid, tint })` sets it inline
   * from the record's own status (`PST[k].solid`, Alerts/v1.1 4476). A CSS value, typically a `--svc-st-*` token:
   * `var(--svc-st-problem)`. When set, no colour class is applied and `statusTint` paints the background.
   */
  readonly statusColor = input<string | null>(null);

  /** Tint of the status (background), the `tint` pair of `statusColor`: `var(--svc-st-problem-tint)`. */
  readonly statusTint = input<string | null>(null);

  protected readonly isDesignSystem = computed(() => this.variant() === 'design-system');

  protected readonly isCard = computed(() => this.variant() === 'card');

  protected readonly hasHostMinWidth = computed(() => this.variant() !== 'pill' && !this.isCard());

  protected readonly badgeClass = computed(() => {
    if (this.statusColor()) return '';
    const type = this.badgeData()?.type ?? '';
    return this.colorClass() ?? this.#variantColorClass(type) ?? this.resolveBadgeClass(type);
  });

  #variantColorClass(type: string): string | undefined {
    if (this.isDesignSystem()) return DESIGN_SYSTEM_BADGE_CLASSES[type];
    if (this.isCard()) return CARD_BADGE_CLASSES[type];
    return undefined;
  }

  protected get badgeClasses(): string[] {
    const shapeClass = this.resolveShapeClass();
    return [shapeClass, this.badgeClass(), this.textClass()].filter((c): c is string => !!c);
  }

  private resolveShapeClass(): string {
    if (this.isCard()) {
      return 'inline-flex h-5 items-center justify-center whitespace-nowrap rounded-full border px-[9px] py-0.5 text-[11px] font-semibold leading-[14px]';
    }
    if (this.variant() === 'pill') {
      return 'inline-flex items-center justify-center whitespace-nowrap rounded-full border px-3 py-[3px] text-xs font-semibold leading-5 max-[1023px]:px-2.5 max-[1023px]:text-[11px]';
    }
    if (this.isDesignSystem()) {
      return 'inline-flex min-w-[74px] items-center justify-center whitespace-nowrap rounded-full border px-[15px] py-1 text-xs font-semibold leading-5';
    }
    if (this.pill()) {
      return 'min-w-[74px] rounded-full border px-[15px] py-1 text-xs font-semibold leading-5';
    }
    return 'rounded-lg px-2 py-1 text-sm font-normal leading-tight';
  }

  protected get description(): string {
    return this.badgeData()?.description?.trim() || '-';
  }

  private resolveBadgeClass(type?: string | null): string {
    switch (type) {
      case 'none':
        return 'bg-slate-50 text-default';
      case 'waiting':
      case 'inactive':
      case 'rejected':
        return 'bg-[#A6A6A6] text-white';
      case 'disaproved':
      case 'delayed':
      case 'late':
        return 'bg-[#E22A2E] text-white';
      case 'expired':
        return 'bg-[#CC3300] text-white';
      case 'opened':
      case 'pending':
      case 'revision':
      case 'analysis':
      case 'investigation':
      case 'ontime':
      case 'elaboration':
      case 'inElaboration':
        return 'bg-[#FFC000] text-white';
      case 'planned':
      case 'revised':
      case 'resolution':
      case 'liberation':
      case 'investigated':
      case 'confirmation':
      case 'inAttention':
        return 'bg-[#BF8F00] text-white';
      case 'homologation':
      case 'execution':
      case 'classification':
      case 'scheduled':
        return 'bg-[#806000] text-white';
      case 'planned-without-delay':
      case 'avaliation':
      case 'released':
      case 'approval':
      case 'validated':
      case 'validation':
      case 'inProcess':
      case 'onTrack':
        return 'bg-[#A9D08E] text-white';
      case 'active':
      case 'completed':
      case 'published':
      case 'resolved':
      case 'executed':
      case 'sent':
      case 'sended':
        return 'bg-[#466900] text-white';
      case 'cmms':
        return 'bg-[#715500] text-white';
      case 'subscribe':
        return 'bg-[#2196F3] text-white';
      case 'closed':
        return 'bg-[#9BA0A3] text-white';
      default:
        return '';
    }
  }
}
